// #397 — the G100 Test Console's Add a device card. An IMEI, a name and a region make the Camera,
// the G100Device and the broker login on the server; the camera can log in within seconds. The
// server's per-field reasons show under the fields; a provisioning step that failed shows as a
// warning (the rows are in place, pressing Add again retries it).
import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { g100ConsoleApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import type { G100AddDeviceRequest, G100AddDeviceResult } from '@/types'

const FIELD = 'w-full rounded border border-border bg-background px-2 py-1 text-sm'
const statusOf = (error: unknown) => (error as { response?: { status?: number } })?.response?.status
const fieldErrors = (error: unknown): Record<string, string> => {
  const errors = (error as { response?: { data?: { errors?: Record<string, unknown> } } })?.response?.data?.errors
  return errors ? Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, String(v)])) : {}
}

export function AddDeviceCard({ onAdded }: { onAdded: (camera: G100AddDeviceResult['camera']) => void }) {
  const { t } = useTranslation('g100Console')
  const qc = useQueryClient()
  const [imei, setImei] = useState('')
  const [name, setName] = useState('')
  const [region, setRegion] = useState<1 | 4>(4)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)

  const add = useMutation({
    mutationFn: (body: G100AddDeviceRequest) => g100ConsoleApi.addDevice(body),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: queryKeys.g100Console.cameras() })
      setWarning(result.warning)
      // A warned add keeps the fields (and says no success): Add again retries the failed step.
      if (!result.warning) {
        toast.success(t(result.created ? 'add.added' : 'add.repeat', { name: result.camera.name, imei: result.camera.imei }))
        setImei('')
        setName('')
      }
      onAdded(result.camera)
    },
    onError: (error) => {
      const status = statusOf(error)
      if (status === 400 || status === 409) {
        const reasons = fieldErrors(error)
        if (['imei', 'name', 'region'].some((k) => reasons[k])) setErrors(reasons)
        else setFormError(t('add.failed'))
      }
      else if (status === 403) setFormError(t('add.forbidden'))
      else if (status === 429) setFormError(t('actions.throttled'))
      else setFormError(t('add.failed'))
    },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setErrors({})
    setFormError(null)
    setWarning(null)
    add.mutate({ imei: imei.trim(), name: name.trim(), region })
  }
  const blocked = add.isPending || imei.trim() === '' || name.trim() === ''
  const fieldError = (key: string) =>
    errors[key] ? <p role="alert" className="mt-1 text-xs text-destructive">{errors[key]}</p> : null

  return (
    <Card>
      <CardHeader><CardTitle>{t('add.title')}</CardTitle></CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground">{t('add.hint')}</p>
        <form aria-label={t('add.title')} onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-start">
          <div>
            <label htmlFor="g100-add-imei" className="mb-1 block text-sm font-medium">{t('add.imei')}</label>
            <input
              id="g100-add-imei" className={FIELD} value={imei} inputMode="numeric" maxLength={17}
              onChange={(e) => setImei(e.target.value)}
            />
            {fieldError('imei')}
          </div>
          <div>
            <label htmlFor="g100-add-name" className="mb-1 block text-sm font-medium">{t('add.name')}</label>
            <input
              id="g100-add-name" className={FIELD} value={name} maxLength={200}
              onChange={(e) => setName(e.target.value)}
            />
            {fieldError('name')}
          </div>
          <div>
            <label htmlFor="g100-add-region" className="mb-1 block text-sm font-medium">{t('add.region')}</label>
            <select
              id="g100-add-region" className={FIELD} value={region}
              onChange={(e) => setRegion(Number(e.target.value) === 1 ? 1 : 4)}
            >
              <option value={4}>{t('add.regions.4')}</option>
              <option value={1}>{t('add.regions.1')}</option>
            </select>
            {fieldError('region')}
          </div>
          <div className="sm:pt-6">
            <Button type="submit" disabled={blocked}>
              {add.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />}
              {t('add.button')}
            </Button>
          </div>
        </form>
        {formError && <p role="alert" className="mt-2 text-sm text-destructive">{formError}</p>}
        {warning && (
          <p
            role="status"
            className={cn('mt-2 flex items-start gap-1.5 rounded bg-amber-500/15 px-2 py-1 text-sm text-amber-800 dark:text-amber-300')}
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{t('add.warning', { reason: warning })}</span>
          </p>
        )}
      </CardContent>
    </Card>
  )
}
